/**
 * HandoverToken — one slice `OrderAccess` token per handover, at
 * `workspaces/{orgId}/events/{eventId}/handoverTokens/{handoverId}`.
 * Declares zero actions — no `read`, no `write` of any kind — so the emitted
 * rules carry no `match` block for this path at all and the default-deny
 * tail (`match /{document=**} { allow read, write: if false; }`) covers it.
 *
 * A default-denied sibling rather than a field on `Handover`, the same
 * containment `wallet-token.entity.ts` runs for the same reason: Handover is
 * read and listed by the `tickets.checkin.scan` role and Firestore has no
 * field-level ACL, so a token carried there would reach every door phone.
 * The exposure that closes is not disclosure but authority — possession of
 * an access token is what `handTicketsOver` and `takeTicketsBack` authorize
 * on, so a token on a door-readable document would hand any scanning
 * account the power to re-route or reclaim a handed-over ticket. Only the
 * Admin SDK reads this one.
 *
 * No `handoverId` field. WalletToken restates its own doc id because
 * Apple's web service arrives holding a bare serial and
 * `FieldPath.documentId()` cannot be filtered on in a collection-group
 * query — the unknown that field resolves. Nothing here has that problem:
 * every reader arrives holding the token and queries on `accessToken`, then
 * takes the rest off the matched document's own path — the event from
 * `doc.ref.parent.parent`, and the handover from `doc.id`, which is how a
 * slice token reaches the record naming its tickets. Adding the field back
 * by symmetry with WalletToken would restate what the path already answers.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import { SecretToken } from "./ticket.entity.ts";
import type { Actor } from "./workspace.entity.ts";

export const HandoverTokenSchema = z.object({
	accessToken: SecretToken.describe(
		"this handover's own slice OrderAccess token — the recipient opens `workspaces/{orgId}/orderAccess/{token}` by possession alone, reaching only the tickets their handover names, never the rest of the order or its § 14 receipt; it is also the value resolveWalletSaveLinks' collection-group fallback filters on to recover a slice's event",
	),
});
export type HandoverToken = z.infer<typeof HandoverTokenSchema>;

export const HandoverToken = entity<Actor>()({
	schema: HandoverTokenSchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/handoverTokens/{handoverId}",
		}),
	);
